import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import ReactGa from "react-ga4";
import AuthContext from './contexts/AuthContext';
import AdminContext, {
  checkisUnauthorized,
  redirectToLogin,
  redirectToLoginUrl
} from './contexts/AdminContext';
import ErrorBoundary from './components/ErrorBoundary';
import useApi from './hooks/useApi';
import loadingGif from './assets/images/loading-equalizer.gif';
import TourHeaderPhoto from './assets/images/demo-tour-header.png';
import './index.css';
import FullRedirect from './components/FullRedirect';
import NotFound from './components/NotFound';
import constants from './constants';

const Auth = React.lazy(() => import('./pages/Auth'));
const InternalRequest = React.lazy(() => import('./pages/InternalRequest'));
const Campaigns = React.lazy(() => import('./pages/Campaigns'));
const UserForm = React.lazy(() => import('./pages/UserForm'));
const RequestPaymentLink = React.lazy(() => import('./pages/RequestPaymentLink'));
const RequestAxsClaimLink = React.lazy(() => import('./pages/RequestAxsClaimLink'));
const RequestReferralLink = React.lazy(() => import('./pages/InternalRequest/RequestReferralLink'));
const UserManageAttendees = React.lazy(() => import('./pages/UserManageAttendees'));

const PresaleDemo = React.lazy(() => import('./pages/demos/Presale'));
const WaitlistDemo = React.lazy(() => import('./pages/demos/Waitlist'));
const TourDemo = React.lazy(() => import('./pages/demos/Tour'));

const PresaleInsights = React.lazy(() => import('./pages/PresaleInsights'));
const PresaleGroupInsights = React.lazy(() => import('./pages/PresaleGroupInsights'));
const WaitlistInsights = React.lazy(() => import('./pages/WaitlistInsights'));
const WaitlistGroupInsights = React.lazy(() => import('./pages/WaitlistGroupInsights'));
const RegistrationFormInsights = React.lazy(() => import('./pages/RegistrationFormInsights'));

ReactGa.initialize(constants.gaMeasurementId);

function DemoRoutes() {
  return (
    <Switch>
      <Route
        path="/demos/presale"
        exact
        component={PresaleDemo}
      />
      <Route
        path="/demos/waitlist"
        exact
        component={WaitlistDemo}
      />
      <Route
        path="/demos/tour/presale"
        exact
      >
        <PresaleDemo headerImg={TourHeaderPhoto} />
      </Route>
      <Route
        path="/demos/tour/waitlist"
        exact
      >
        <WaitlistDemo headerImg={TourHeaderPhoto} />
      </Route>
      <Route
        path="/demos/tour"
        exact
        component={TourDemo}
      />
      <Redirect to="/demos/presale" />
    </Switch>
  )
}

function NeedsAuthentication() {
  const {
    value: session,
    loading,
    complete,
    execute: refetchSession,
    setValue: updateSession
  } = useApi({
    path: '/admin_session'
  });

  return (
    <AuthContext.Provider
      value={{
        session: session,
        reload: refetchSession,
        update: updateSession,
        loading,
        complete
      }}
    >
      {loading && !complete && (
        <div className="min-h-screen flex flex-col justify-center text-white items-center">
          <img src={loadingGif} />
        </div>
      )}
      {complete && (
        <Switch>
          <Route path="/admin/auth" render={(props) => <Auth {...props} />}></Route>

          <Route
            path="/admin/internal-request/:id"
            render={(props) => (
              <AdminContext.Provider
                value={{
                  session,
                  checkisUnauthorized,
                  redirectToLogin,
                  redirectToLoginUrl
                }}
              >
                <InternalRequest {...props} />
              </AdminContext.Provider>
            )}
          ></Route>

          <Route
            path="/admin/request-referral-link/:invite_id"
            render={(props) => <RequestReferralLink {...props} />}
          ></Route>

          <Route
            path="/admin/campaigns"
            render={(props) => (
              <AdminContext.Provider
                value={{
                  session,
                  checkisUnauthorized,
                  redirectToLogin,
                  redirectToLoginUrl
                }}
              >
                <Campaigns {...props} />
              </AdminContext.Provider>
            )}
          ></Route>

          <Route
            path="/admin" 
          >
            <Redirect to="/admin/campaigns" />
          </Route>         

          <Route path="/*" component={NotFound} />
        </Switch>
      )}
    </AuthContext.Provider>
  );
}

function App() {

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Switch>
              <Route path="/ref/:short_code" component={UserForm} />
              <Route path="/pay/:id" component={RequestPaymentLink} />
              <Route path="/connect-axs/:id" component={RequestAxsClaimLink} />
              <Route path="/manage-attendees/:request_id" component={UserManageAttendees} />

              <Route
                path="/presale-insights/:presale_id"
                render={(props) => <PresaleInsights {...props} />}
              />
              <Route
                path="/waitlist-insights/:waitlist_id"
                render={(props) => <WaitlistInsights {...props} />}
              />
              <Route
                path="/registration-insights/:form_id"
                render={(props) => <RegistrationFormInsights {...props} />}
              />
              <Route
                path="/tour-presale-insights/:presale_group_id"
                render={(props) => <PresaleGroupInsights {...props} />}
              />
              <Route
                path="/tour-waitlist-insights/:waitlist_group_id"
                render={(props) => <WaitlistGroupInsights {...props} />}
              />

              <Route path="/demos">
                <DemoRoutes />
              </Route>

              <NeedsAuthentication />
            </Switch>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
